<template>
  <c-m-sidebar v-if="currentMonitor" :title="$helpers.mask('patient_name', currentMonitor.patient.display_name)"
               :subtitle="$helpers.mask('mrn', currentMonitor.patient.mrn)">
    <template #header-buttons>
      <button
        v-if="currentMonitor.status == 'active' && currentMonitor.room_id && $can('AccessLiveVideo', 'Meeting')"
        class="button is-white is-small video-visit-btn is-outlined"
        @click="openVideoVisitPage"
        v-tooltip="offlineDeviceTooltip(currentMonitor)"
        :disabled="isVideoDeviceOffline(currentMonitor)">
          <span class="icon">
            <i class="mdi mdi-video-outline"></i>
          </span>
        <span>Video Visit</span>
      </button>
    </template>
    <template #content>
      <template v-if="eventOnlyMode">
        <alarm :monitor_id="currentMonitor.id" is-inline @close="$emit('close')"></alarm>
      </template>
      <template v-else>
        <div class="carousel-collapse" v-if="$store.state.currentUser.enabled_flags.includes('patient_insights_dynamic_blocks')">
          <b-collapse animation="fade" v-model="isCarouselOpen">
            <template #trigger>
              <span>Patient Insights</span>
            </template>
            <quickview-blocks-carousel v-if="currentMonitor" :monitor="currentMonitor"></quickview-blocks-carousel>
          </b-collapse>
        </div>
        <quickview-room-mismatch v-if="currentMonitor" :monitor="currentMonitor"></quickview-room-mismatch>
        <draggable v-model="widgets" item-key="id" @change="orderChanged" filter="input" :preventOnFilter="false">
          <template #item="{element: widget}">
            <component v-show="fullScreenComponent.length == 0 || isExpanded(widget.componentName)" :class="{'widget-expanded': this.isExpanded(widget.componentName)}" :is="widget.componentName" v-bind="widget.attrs">
              <template #headerUtils>
                <b-checkbox-button v-model="fullScreenComponent" :native-value="widget.componentName" type="is-primary" class="expand-colapse-btn">
                  <i class="mdi mdi-arrow-expand"></i>
                </b-checkbox-button>
              </template>
            </component>
          </template>
        </draggable>
      </template>
    </template>
  </c-m-sidebar>
</template>

<script>
import WidgetModel from "@/models/WidgetModel";
import Card from "@/components/card/Card";
import FormSubmissionListCard from "@/components/form_builder/FormSubmissionListCard";
import Announcement from "@/components/announcement/Announcement";
import Alarm from "@/components/Alarm";
import EventsCard from "@/components/EventsCard";
import CMSidebar from "@/components/safety_center/CMSidebar";
import draggable from "vuedraggable";
import PatientTags from "@/components/PatientTags.vue";
import PatientNote from "@/components/PatientNote.vue";
import AwayMessageEditCard from "@/components/AwayMessageEditCard.vue";
import QuickviewRoomMismatch from "@/components/quickview/QuickviewRoomMismatch.vue";
import QuickviewBlocksCarousel from "@/components/quickview/QuickviewBlocksCarousel.vue";
import { isVideoDeviceOffline, offlineDeviceTooltip } from '@/lib/PatientUtils';
import to from '@/lib/to';

export default {
  name: "QuickView",
  components: {
    QuickviewBlocksCarousel,
    QuickviewRoomMismatch,
    PatientTags, PatientNote, AwayMessageEditCard,
    CMSidebar, Alarm, EventsCard, Announcement, FormSubmissionListCard, Card, draggable},
  props: {
    monitor: {
      type: Object
    },
    monitorId: {
      type: [Number, String]
    },
    patientId: {
      type: [Number, String]
    },
    eventOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentMonitor() {
      return this.monitor || this.fetchedMonitor
    },
    eventsModel() {
      if (!this.currentMonitor) return null
      const model = new WidgetModel('patient-events', {}, {})
      model.id = Math.random()
      model.monitor = this.currentMonitor
      model.patientId = this.currentMonitor.patient_id
      model.monitorId = this.currentMonitor.id
      return model
    }
  },
  methods: {
    async fetchMonitor() {
      if (this.monitorId && this.$can('View', 'PatientMonitor')) {
        const [err, response] = await to(this.$http.get(`/patient_monitors/${this.monitorId}`))
        if (!err) {
          this.fetchedMonitor = response.data
        }
      }
    },
    buildWidgetsArray() {
      if (!this.currentMonitor) return
      this.widgets = [
        { componentName: 'patientTags', attrs: { patient: this.currentMonitor.patient, monitorId: this.currentMonitor.id } },
        { componentName: 'patientNote', attrs: { patient: this.currentMonitor.patient, monitorId: this.currentMonitor.id } },
        { componentName: 'awayMessageEditCard', attrs: { monitor: this.currentMonitor } },
        {
          componentName: 'eventsCard',
          attrs: {
            widgetModel: this.eventsModel,
            cardAttrs: { autoHeight: true, title: 'Patient Events', hasSubtitle: false, maintainAspectRatio: false },
            handleNewEvent: true,
            onNewEventOpen: this.newEventOpen
          }
        },
        { componentName: 'announcement', attrs: { monitorId: this.currentMonitor.id, cardAttrs: { shit: true }} },
        {
          componentName: 'formSubmissionListCard',
          attrs: {
            patientMonitorId: this.currentMonitor.id,
            patientId: this.currentMonitor.patient_id,
            cardAttrs: { autoHeight: true },
          }
        },
      ]
      this.orderWidgets()
    },
    openVideoVisitPage() {
      this.$router.push({ name: 'patient-monitors.monitoring', params: { id: this.currentMonitor.id }, query: { 'start-video': true } })
    },
    newEventOpen() {
      this.eventOnlyMode = true
    },
    orderWidgets() {
      const pref = this.$store.getters['UserPreferencesStore/getPreference']('quickview')
      const order = pref?.widget_order
      if (order && Array.isArray(order)) {
        this.widgets = this.widgets.sort((a, b) => {
          if (order.indexOf(a['componentName']) > order.indexOf(b['componentName'])) {
            return 1
          } else {
            return -1
          }
        })
      }
    },
    orderChanged() {
      const widgetsOrder = this.widgets.map(x => x.componentName)
      this.$store.dispatch('UserPreferencesStore/set', {
        key: 'quickview',
        value: { widget_order: widgetsOrder }
      })
    },
    isExpanded(componentName) {
      return this.fullScreenComponent[0] == componentName
    },
    isVideoDeviceOffline, offlineDeviceTooltip
  },
  async beforeMount() {
    if (!this.monitor && this.monitorId) {
      await this.fetchMonitor()
    }
    this.buildWidgetsArray()
  },
  watch: {
    monitor() {
      this.reloadKey = Math.random()
      this.buildWidgetsArray()
    },
    async monitorId() {
      if (!this.monitor && this.monitorId) {
        await this.fetchMonitor()
        this.buildWidgetsArray()
      }
    },
    currentMonitor() {
      this.buildWidgetsArray()
    },
    eventOnly: {
      handler(value) {
        this.eventOnlyMode = value
      },
      immediate: true
    }
  },
  data() {
    return {
      reloadKey: Math.random(),
      eventOnlyMode: false,
      widgets: [],
      widgetRefs: {},
      fullScreenComponent: [],
      isCarouselOpen: false,
      fetchedMonitor: null
    }
  }
}
</script>

<style scoped lang="scss">
:deep() {
  .quickview__widgets {
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
    > div > div {
      margin-bottom: 30px;
    }
    :deep(.patient-events, .form-submission-card .monitoring-card__content > div) {
      max-height: 200px;
      overflow-y: auto !important;
    }
  }
  .events-modal {
    margin-bottom: 0 !important;
  }
  .expand-colapse-btn {
    margin-left: 5px;
  }
  .carousel-collapse {
    margin-bottom: 30px;
    .collapse-trigger {
      display: block;
      background: color-mix(in srgb, var(--ld-brand-primary) 32%, transparent);
      padding: 15px;
      font-size: 13px;
      font-weight: 500;
      border-radius: 4px;
      letter-spacing: 0.4px;
    }
    .collapse-content {
      margin-top: -5px;
    }
    > div {
      margin-bottom: 0 !important;
    }
  }
}
.widget-expanded {
  :deep(.monitoring-card__content > div) {
    max-height: 100%;
    overflow-y: hidden !important;
  }
}
</style>
